import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SacredGeometry from '../components/SacredGeometry';
import AnimatedBackground from '../components/AnimatedBackground';

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="w-full">
      {/* HERO SECTION */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        {/* Multi-layer background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-700 to-lavender-600"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-violet-800/50 via-transparent to-purple-600/30"></div>
        
        {/* Animated background elements */}
        <AnimatedBackground opacity={0.6} />
        
        {/* Sacred geometry background */}
        <SacredGeometry opacity={0.06} />
        
        {/* Content */}
        <div className="relative z-10 text-center px-6 py-20 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-4 animate-fade-in text-glow">
            Sylvaine
          </h1>
          <p className="text-lg md:text-xl text-purple-100 animate-fade-in-up font-light" style={{ animationDelay: '100ms' }}>
            Un parcours au service de l'âme et du vivant
          </p>
        </div>
      </section>

      {/* MAIN CONTENT SECTION */}
      <section className="relative py-20 md:py-32 px-6 bg-gradient-to-b from-white via-purple-50/10 to-white">
        {/* Subtle background geometry */}
        <div className="absolute inset-0 overflow-hidden">
          <SacredGeometry opacity={0.015} />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left: Photo */}
            <div className="animate-fade-in-up">
              <div className="sticky top-24">
                <img
                  src="/images/sylvaine.jpg"
                  alt="Sylvaine Ben Klif"
                  className="w-full max-w-lg mx-auto rounded-3xl shadow-glow border-4 border-purple-100/50 object-cover"
                />
              </div>
            </div>
            
            {/* Right: Text Content */}
            <div className="space-y-6 text-gray-700 leading-relaxed text-base md:text-lg animate-fade-in-up" style={{ animationDelay: '200ms' }}>
              <p>
                Bonjour, je m'appelle <span className="font-semibold text-purple-900">Sylvaine</span>, j'ai 58 ans et j'habite dans le Nord vaudois.
              </p>
              
              <p>
                Voilà plus de 20 ans que je me forme et que j'explore différentes façons de guérir le corps et l'âme. Détentrice de plusieurs diplômes, dont celui de naturopathe, j'aime mettre mes connaissances au service des autres afin de trouver des solutions et accompagner chacun dans la découverte de soi, de l'âme et de la spiritualité.
              </p>
              
              <p>
                Spécialisée dans les <span className="font-semibold text-purple-900">constellations familiales</span>, j'aime regarder au-delà de ce que nous percevons habituellement et oser aller plus en profondeur, toujours avec une écoute attentive et bienveillante.
              </p>
              
              <p>
                Je ressens également un lien particulier et puissant avec les <span className="font-semibold text-purple-900">animaux</span>, qui me permet d'être au plus proche d'eux, tant sur le plan émotionnel que subtil. La joie de retrouver un animal perdu est indescriptible. Je me souviens notamment d'un cheval disparu pendant trois jours d'inquiétude : les retrouvailles ont été l'un des plus beaux moments que j'ai vécus avec un animal.
              </p>
              
              <p>
                Je me décris comme une personne simple, qui aime voyager et aller à la rencontre de différentes formes de spiritualité. J'aime rire et partager des moments sincères avec ceux qui me sont chers.
              </p>
              
              <p className="pt-4">
                N'hésitez pas à me contacter, même anonymement. Je vous accueillerai toujours avec bienveillance, respect et écoute.
              </p>
              
              <p className="italic text-purple-800 font-medium pt-2">
                À tout bientôt
              </p>
              
              {/* CTA Button */}
              <div className="pt-8">
                <Link
                  to="/contact"
                  className="inline-block px-10 py-4 bg-gradient-to-r from-purple-600 to-lavender-600 text-white rounded-full hover:shadow-glow-lg transition-all duration-300 font-medium text-lg transform hover:scale-105"
                >
                  Me contacter
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE SECTION */}
      <section className="relative py-24 md:py-32 px-6 overflow-hidden">
        {/* Multi-layer background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-800 via-purple-600 to-lavender-500"></div>
        <div className="absolute inset-0 bg-gradient-to-tl from-violet-700/40 via-transparent to-purple-700/30"></div>
        
        {/* Animated background elements */}
        <AnimatedBackground opacity={0.5} />
        
        {/* Sacred geometry */}
        <SacredGeometry opacity={0.05} />
        
        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-medium text-white leading-relaxed text-glow italic px-6">
            "Accompagner chacun dans la découverte de soi, de l'âme et de la spiritualité"
          </h2>
        </div>
      </section>
    </div>
  );
};

export default About;
